import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { studentsApi, classesApi } from '../../services/api';
import api from '../../services/api';
import BulkPromoteDialog from './BulkPromoteDialog';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Users, Loader2, Download, TrendingUp } from 'lucide-react';

const StudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [bulkPromoteOpen, setBulkPromoteOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    roll_number: '',
    class_id: '',
    parent_email: '',
    father_name: '',
    mother_name: '',
    phone: '',
    address: '',
    date_of_birth: '',
    place_of_birth: '',
    pen_number: '',
    sts_number: '',
    religion: '',
    caste: '',
    sub_caste: '',
    gender: '',
    uses_transport: false,
    transport_fee: 0,
  });

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    loadStudents();
  }, [page, search, selectedClass]);

  const loadClasses = async () => {
    try {
      const response = await classesApi.getAll();
      setClasses(response.data.classes || []);
    } catch (error) {
      console.error('Failed to load classes:', error);
    }
  };

  const loadStudents = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (selectedClass) params.class_id = selectedClass;
      
      const response = await studentsApi.getAll(params);
      setStudents(response.data.students || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingStudent) {
        await studentsApi.update(editingStudent.id, formData);
        toast.success('Student updated successfully');
      } else {
        await studentsApi.create(formData);
        toast.success('Student created successfully');
      }
      setIsModalOpen(false);
      resetForm();
      loadStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name || '',
      email: student.email || '',
      roll_number: student.roll_number || '',
      class_id: student.class_id || '',
      parent_email: student.parent_email || '',
      father_name: student.father_name || '',
      mother_name: student.mother_name || '',
      phone: student.phone || '',
      address: student.address || '',
      date_of_birth: student.date_of_birth || '',
      place_of_birth: student.place_of_birth || '',
      pen_number: student.pen_number || '',
      sts_number: student.sts_number || '',
      religion: student.religion || '',
      caste: student.caste || '',
      sub_caste: student.sub_caste || '',
      gender: student.gender || '',
      uses_transport: !!student.uses_transport,
      transport_fee: student.transport_fee || 0,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    
    try {
      await studentsApi.delete(id);
      toast.success('Student deleted successfully');
      loadStudents();
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      roll_number: '',
      class_id: '',
      parent_email: '',
      father_name: '',
      mother_name: '',
      phone: '',
      address: '',
      date_of_birth: '',
      place_of_birth: '',
      pen_number: '',
      sts_number: '',
      religion: '',
      caste: '',
      sub_caste: '',
      gender: '',
      uses_transport: false,
      transport_fee: 0,
    });
    setEditingStudent(null);
  };

  const totalPages = Math.ceil(total / 10);

  return (
    <div className="space-y-6" data-testid="students-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Students</h1>
          <p className="text-muted-foreground mt-1">Manage student records</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkPromoteOpen(true)}
            data-testid="bulk-promote-button"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Bulk Promote
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                toast.info('Preparing Excel...');
                const res = await api.get('/students/export', { responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `students_${new Date().toISOString().slice(0, 10)}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success('Excel downloaded');
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Export failed');
              }
            }}
            data-testid="export-students-excel-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Excel
          </Button>
          <Dialog open={isModalOpen} onOpenChange={(open) => { setIsModalOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-student-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Student
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStudent ? 'Edit Student' : 'Add New Student'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="student-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="student-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="roll_number">Roll Number *</Label>
                  <Input
                    id="roll_number"
                    value={formData.roll_number}
                    onChange={(e) => setFormData({ ...formData, roll_number: e.target.value })}
                    required
                    data-testid="student-roll-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="class_id">Class *</Label>
                  <Select value={formData.class_id} onValueChange={(value) => setFormData({ ...formData, class_id: value })}>
                    <SelectTrigger data-testid="student-class-select">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map((cls) => (
                        <SelectItem key={cls.id} value={cls.id}>
                          {cls.name} {cls.section && `- ${cls.section}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="parent_email">Parent Email</Label>
                  <Input
                    id="parent_email"
                    type="email"
                    value={formData.parent_email}
                    onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                    data-testid="student-parent-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="student-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select value={formData.gender || "none"} onValueChange={(value) => setFormData({ ...formData, gender: value === "none" ? "" : value })}>
                  <SelectTrigger data-testid="student-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Parents (Father / Mother) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="father_name">Father's Name</Label>
                  <Input
                    id="father_name"
                    value={formData.father_name}
                    onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                    data-testid="student-father-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mother_name">Mother's Name</Label>
                  <Input
                    id="mother_name"
                    value={formData.mother_name}
                    onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                    data-testid="student-mother-name-input"
                  />
                </div>
              </div>

              {/* Birth details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    data-testid="student-dob-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="place_of_birth">Place of Birth</Label>
                  <Input
                    id="place_of_birth"
                    value={formData.place_of_birth}
                    onChange={(e) => setFormData({ ...formData, place_of_birth: e.target.value })}
                    placeholder="e.g. Belagavi"
                    data-testid="student-pob-input"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="House / Street / Village / City"
                  data-testid="student-address-input"
                />
              </div>

              {/* Identifiers: PEN & STS */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pen_number">PEN Number</Label>
                  <Input
                    id="pen_number"
                    value={formData.pen_number}
                    onChange={(e) => setFormData({ ...formData, pen_number: e.target.value })}
                    placeholder="Permanent Education Number"
                    data-testid="student-pen-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sts_number">STS Number</Label>
                  <Input
                    id="sts_number"
                    value={formData.sts_number}
                    onChange={(e) => setFormData({ ...formData, sts_number: e.target.value })}
                    placeholder="Student Tracking System no."
                    data-testid="student-sts-input"
                  />
                </div>
              </div>

              {/* Religion & Caste */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="religion">Religion</Label>
                  <Input
                    id="religion"
                    value={formData.religion}
                    onChange={(e) => setFormData({ ...formData, religion: e.target.value })}
                    placeholder="e.g. Hindu / Muslim / Christian"
                    data-testid="student-religion-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="caste">Caste</Label>
                  <Input
                    id="caste"
                    value={formData.caste}
                    onChange={(e) => setFormData({ ...formData, caste: e.target.value })}
                    placeholder="e.g. General / SC / ST / OBC"
                    data-testid="student-caste-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub_caste">Sub-Caste</Label>
                <Input
                  id="sub_caste"
                  value={formData.sub_caste}
                  onChange={(e) => setFormData({ ...formData, sub_caste: e.target.value })}
                  placeholder="e.g. Lingayat, Kuruba, ..."
                  data-testid="student-subcaste-input"
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="uses_transport"
                  checked={!!formData.uses_transport}
                  onChange={(e) => setFormData({ ...formData, uses_transport: e.target.checked })}
                  className="h-4 w-4 accent-primary cursor-pointer"
                  data-testid="student-uses-transport-checkbox"
                />
                <Label htmlFor="uses_transport" className="cursor-pointer">
                  Uses school transport (bus)
                </Label>
              </div>
              {formData.uses_transport && (
                <div className="space-y-2">
                  <Label htmlFor="transport_fee">
                    Annual Transport Fee for this student (₹)
                  </Label>
                  <Input
                    id="transport_fee"
                    type="number"
                    min={0}
                    step={1}
                    value={formData.transport_fee}
                    onChange={(e) =>
                      setFormData({ ...formData, transport_fee: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="e.g. 12000"
                    data-testid="student-transport-fee-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Each student can have a different transport fee (varies by distance / route).
                    Leave 0 if not applicable.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="student-submit-button">
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingStudent ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-students-input"
              />
            </div>
            <Select value={selectedClass || "all"} onValueChange={(val) => setSelectedClass(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-class-select">
                <SelectValue placeholder="All Classes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {classes.map((cls) => (
                  <SelectItem key={cls.id} value={cls.id}>
                    {cls.name} {cls.section && `- ${cls.section}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Student List ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner h-8 w-8 text-primary" />
            </div>
          ) : students.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Roll No.</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Gender</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((student) => {
                      const cls = classes.find((c) => c.id === student.class_id);
                      const className = cls
                        ? `${cls.name}${cls.section ? ` - ${cls.section}` : ''}`
                        : (student.class_name || '-');
                      return (
                      <TableRow key={student.id} className="table-row-hover">
                        <TableCell className="font-medium">{student.name}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>{student.roll_number}</TableCell>
                        <TableCell>{className}</TableCell>
                        <TableCell className="capitalize">{student.gender || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(student)}
                            data-testid={`edit-student-${student.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(student.id)}
                            data-testid={`delete-student-${student.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No students found</p>
            </div>
          )}
        </CardContent>
      </Card>

      <BulkPromoteDialog
        open={bulkPromoteOpen}
        onOpenChange={setBulkPromoteOpen}
        currentAcademicYear="2026-27"
        onDone={loadStudents}
      />
    </div>
  );
};

export default StudentsPage;
